import React, { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2, DollarSign, CreditCard,
  ArrowLeft, Shield, LogOut, BookOpen,
  Menu, X, Server, ExternalLink
} from 'lucide-react';
import { useBusinessContext } from '../contexts/BusinessContext';

const AdminLayout = () => {
  const navigate = useNavigate();
  const { logout, user } = useBusinessContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get API base URL from environment (remove /api suffix for Hangfire)
  const getHangfireUrl = () => {
    const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://localhost:7086';
    // Remove /api suffix if present to get base URL
    const baseUrl = apiUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/hangfire`;
  };

  const navItems = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, end: true },
    { name: 'Businesses', href: '/admin/businesses', icon: Building2 },
    { name: 'Financials', href: '/admin/financials', icon: DollarSign },
    { name: 'Expenses', href: '/admin/expenses', icon: CreditCard },
    { name: 'Documentation', href: '/admin/documentation', icon: BookOpen },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-gray-900 text-white">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <Shield className="text-white" size={16} />
            </div>
            <span className="font-bold">Admin Portal</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Slide-out Menu */}
      <div className={`
        lg:hidden fixed top-0 left-0 bottom-0 w-72 bg-gray-900 text-white z-50
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Mobile Menu Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Shield className="text-white" size={20} />
            </div>
            <div>
              <h1 className="font-bold text-lg">Admin Portal</h1>
              <p className="text-xs text-gray-400">SuddyFlow Platform</p>
            </div>
          </div>
          <button
            onClick={closeMobileMenu}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Mobile Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.end}
              onClick={closeMobileMenu}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <item.icon size={20} />
              <span className="font-medium">{item.name}</span>
            </NavLink>
          ))}

          {/* Hangfire Link */}
          <a
            href={getHangfireUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMobileMenu}
            className="flex items-center justify-between px-3 py-3 rounded-lg text-orange-400 hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Server size={20} />
              <span className="font-medium">Hangfire Jobs</span>
            </div>
            <ExternalLink size={14} className="text-gray-500" />
          </a>
        </nav>

        {/* Mobile Menu Footer */}
        <div className="p-4 border-t border-gray-800 space-y-2">
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="font-medium">Back to App</span>
          </Link>

          <button
            onClick={() => { handleLogout(); closeMobileMenu(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <LogOut size={18} />
            <span className="font-medium">Logout</span>
          </button>

          <div className="px-3 pt-2">
            <p className="text-xs text-gray-500">Logged in as</p>
            <p className="text-sm text-gray-300 truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-gray-900 text-white flex-col fixed top-0 left-0 bottom-0">
        {/* Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Shield className="text-white" size={20} />
            </div>
            <div>
              <h1 className="font-bold text-lg">Admin Portal</h1>
              <p className="text-xs text-gray-400">SuddyFlow Platform</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <item.icon size={18} />
              <span className="font-medium">{item.name}</span>
            </NavLink>
          ))}

          {/* Hangfire Link */}
          <a
            href={getHangfireUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-3 py-2.5 rounded-lg text-orange-400 hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Server size={18} />
              <span className="font-medium">Hangfire Jobs</span>
            </div>
            <ExternalLink size={12} className="text-gray-500" />
          </a>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800 space-y-2">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="font-medium">Back to App</span>
          </Link>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <LogOut size={18} />
            <span className="font-medium">Logout</span>
          </button>

          <div className="px-3 pt-2">
            <p className="text-xs text-gray-500">Logged in as</p>
            <p className="text-sm text-gray-300 truncate">{user?.email}</p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 overflow-auto min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;